package one.devsbox.www.ui.devices.detail

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.toComposeImageBitmap
import compose.assets.Svg
import compose.assets.svg.DeleteOutline
import compose.assets.svg.EditOutline
import compose.assets.svg.ListBoxOutline
import compose.assets.svg.OverscanOutline
import kotlinx.coroutines.launch
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.devices.ApplicationType
import one.devsbox.shared.devices.IO
import one.devsbox.www.io.Repository
import one.devsbox.www.ui.widgets.ScreenContentAction
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.ui.widgets.EntryViewMode
import one.devsbox.www.www_portal.generated.resources.*
import tk.mallumo.compose.navigation.navTo_CampaignDUI
import tk.mallumo.compose.navigation.navTo_DeviceEUI
import tk.mallumo.compose.navigation.navTo_LocationDUI
import tk.mallumo.compose.navigation.navTo_ResourceUI
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState

@VM
class DeviceDVM : NavigationViewModel() {
    private lateinit var ctx: DeviceDCtx

    var contentActions by mutableStateOf<List<ScreenContentAction>>(emptyList())
        private set

    data class Data(
        val device: SharedIO.Device,
        val campaigns: SharedIO.Campaigns,
        val latestWwwVersion: Int = 0,
        val latestRuntimeVersion: Int = 0
    )

    var state by mutableStateOf<DataState<Data>>(DataState.Loading())
        private set

    var showDeleteConfirmation by mutableStateOf(false)

    // Screenshot overlay state
    var screenshotState by mutableStateOf<DataState<ImageBitmap>>(DataState.Idle())
        private set

    var onTriggerScreenshot: () -> Unit = {}

    var monitoringState by mutableStateOf<DataState<List<IO.Monitoring>>>(DataState.Idle())
        private set

    interface Action {
        fun reload()
        fun back()

        fun edit()
        fun delete()
        fun confirmDelete()
        fun dismissDeleteConfirmation()

        fun openMedia(photoId: Long)
        fun openCampaign(campaignId: Long)
        fun openLocation(locationId: Long)
    }

    val action = object : Action {
        override fun reload() {
            state = DataState.Loading()
            monitoringState = DataState.Idle()
            loadDeviceDetail()
        }

        override fun back() {
            ctx.nav.up()
        }

        override fun edit() {
            ctx.nav.navTo_DeviceEUI(ctx.args)
        }

        override fun delete() {
            showDeleteConfirmation = true
        }

        override fun dismissDeleteConfirmation() {
            showDeleteConfirmation = false
        }

        override fun confirmDelete() {
            showDeleteConfirmation = false
            state = DataState.Loading(state.entry)
            scope.launch {
                runCatching {
                    Repository.ads.device.delete(ctx.args.id)
                }.onSuccess {
                    ctx.nav.up()
                }.onFailure {
                    state = DataState.Error(it.message ?: "Failed to delete device")
                    setupActions(state)
                }
            }
        }

        override fun openMedia(photoId: Long) {
            if (photoId > 0L) {
                ctx.nav.navTo_ResourceUI(ArgsId(photoId))
            }
        }

        override fun openCampaign(campaignId: Long) {
            ctx.nav.navTo_CampaignDUI(ArgsId(campaignId))
        }

        override fun openLocation(locationId: Long) {
            if (locationId > 0L) {
                ctx.nav.navTo_LocationDUI(ArgsId(locationId))
            }
        }
    }

    fun setup(ctx: DeviceDCtx) {
        this.ctx = ctx
        state = DataState.Loading(state.entry)
        loadDeviceDetail()
    }

    private fun loadDeviceDetail() {
        scope.launch {
            runCatching {
                val id = ctx.args.id
                val device = Repository.ads.device.get(id)
                val campaigns = Repository.ads.campaign.listingByDeviceId(id, 0)
                val apps = Repository.ads.app.listing()
                Triple(device, campaigns, apps)
            }.onSuccess { (device, campaigns, apps) ->
                val latestWww = apps.filter { it.id == device.content.config.applicationId }
                    .maxOfOrNull { it.version } ?: 0
                val latestRuntime = apps.filter { it.id == device.content.config.runtimeId }
                    .maxOfOrNull { it.version } ?: 0
                state = DataState.Result(Data(device, campaigns, latestWww, latestRuntime))
                loadMonitoringHistory()
            }.onFailure {
                state = DataState.Error(it.message ?: "Unknown error")
            }
            setupActions(state)
        }
    }

    private fun loadMonitoringHistory() {
        monitoringState = DataState.Loading()
        scope.launch {
            runCatching {
                Repository.ads.device.historyMonitor(ctx.args.id, 0)
            }.onSuccess { list ->
                val sorted = list.sortedByDescending { it.dt }.take(4)
                monitoringState = DataState.Result(sorted)
            }.onFailure {
                monitoringState = DataState.Error(it.message ?: "Failed to load monitoring data")
            }
        }
    }

    fun takeScreenshot() {
        screenshotState = DataState.Loading()
        scope.launch {
            runCatching {
                val bytes = Repository.ads.device.screenshot(ctx.args.id)
                org.jetbrains.skia.Image.makeFromEncoded(bytes).toComposeImageBitmap()
            }.onSuccess { bitmap ->
                screenshotState = DataState.Result(bitmap)
            }.onFailure {
                it.printStackTrace()
                screenshotState = DataState.Error(it.message ?: "Failed to capture screenshot")
            }
        }
    }

    fun clearScreenshot() {
        screenshotState = DataState.Idle()
    }

    private fun setupActions(result: DataState<Data>) {
        contentActions = result.entry?.device?.run {
            buildList {
                if (ctx.me.permission.device.write) {
                    add(
                        ScreenContentAction(
                            text = Res.string.ui_devices_detail_devicedui_btn_edit,
                            color = ctx.colorSecondaryContainer,
                            icon = Svg.EditOutline
                        ) {
                            action.edit()
                        }
                    )
                    add(
                        ScreenContentAction(
                            text = Res.string.ui_devices_detail_devicedui_btn_device_actions,
                            color = ctx.colorSecondaryContainer,
                            icon = Svg.ListBoxOutline
                        ) {
                            ctx.deviceActions.open()
                        }
                    )
                }
                if (ctx.me.permission.device.read) {
                    add(
                        ScreenContentAction(
                            text = Res.string.ui_devices_detail_devicedui_btn_screenshot,
                            color = ctx.colorPrimaryContainer,
                            icon = Svg.OverscanOutline
                        ) {
                            onTriggerScreenshot()
                        }
                    )
                }
                if (ctx.me.permission.device.delete) {
                    add(
                        ScreenContentAction(
                            text = Res.string.ui_devices_detail_devicedui_btn_delete,
                            color = ctx.colorErrorContainer,
                            icon = Svg.DeleteOutline
                        ) {
                            action.delete()
                        }
                    )
                }
            }
        } ?: emptyList()
    }

    override fun onRelease() {}
}
